import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Archive, CheckCircle2, Clock, Inbox } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useCompanyScope } from '@/lib/company-context';
import { numberFormat } from '@/lib/utils';

interface Summary {
  /** MIXTO = asesor en alguna empresa y coordinador (o más) en otra: solo lo suyo en la primera. */
  scope: 'MIS_LEADS' | 'TODOS' | 'MIXTO';
  companies: number;
  projects: Array<{
    id: string;
    name: string;
    district: string | null;
    companyId: string;
    companyName: string;
    /** Todos los leads del proyecto, incluidos los cerrados / desistidos. */
    total: number;
    /** Sin gestionar + al día + vencidos. */
    activos: number;
    noGestionados: number;
    alDia: number;
    vencidos: number;
    /** Cerrados o desistidos (seguimiento INACTIVO). */
    inactivos: number;
  }>;
}

/** Filtros de la tabla de leads que corresponden a cada cifra del resumen (F3: cada número lleva a su lista). */
const METRIC_FILTER = {
  activos: { status: 'ACTIVO' },
  noGestionados: { followupState: 'NO_GESTIONADO' },
  vencidos: { followupState: 'VENCIDO' },
  alDia: { followupState: 'AL_DIA' },
  inactivos: { followupState: 'INACTIVO' },
} as const;
type MetricKey = keyof typeof METRIC_FILTER;

export function HomePage() {
  const { user } = useAuth();
  const { active, companies } = useCompanyScope();
  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'summary'],
    queryFn: () => api<Summary>('/api/v1/me/summary'),
  });

  // Respeta el selector global de empresa (F3): solo los proyectos de la empresa activa.
  const projects = (data?.projects ?? []).filter((p) => !active || p.companyId === active);
  const totals = data
    ? projects.reduce(
        (acc, p) => ({
          activos: acc.activos + p.activos,
          noGestionados: acc.noGestionados + p.noGestionados,
          alDia: acc.alDia + p.alDia,
          vencidos: acc.vencidos + p.vencidos,
          inactivos: acc.inactivos + p.inactivos,
        }),
        { activos: 0, noGestionados: 0, alDia: 0, vencidos: 0, inactivos: 0 },
      )
    : undefined;
  const tableLink = (metric: MetricKey, scope: { companyId?: string; projectId?: string } = {}) => {
    const companyId = scope.companyId ?? active;
    return `/pipeline?vista=tabla&${new URLSearchParams({
      ...(companyId ? { companyId } : {}),
      ...(scope.projectId ? { projectId: scope.projectId } : {}),
      ...METRIC_FILTER[metric],
    })}`;
  };
  const activeCompanyName = companies.find((c) => c.companyId === active)?.companyName;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Hola, {user?.fullName.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground text-sm">
          {data?.scope === 'MIS_LEADS'
            ? 'Estado de tus leads'
            : data?.scope === 'MIXTO'
              ? 'Estado de los leads de tu alcance (en las empresas donde eres asesor, solo los tuyos)'
              : 'Estado de los leads de tu alcance'}{' '}
          por proyecto{activeCompanyName && companies.length > 1 ? ` en ${activeCompanyName}` : ''}.
          Cada cifra lleva a la lista.
        </p>
      </div>

      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          No se pudo cargar el resumen: {(error as Error).message}
        </p>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Stat
          label="Leads activos"
          value={totals?.activos}
          icon={Inbox}
          loading={isLoading}
          to={tableLink('activos')}
        />
        <Stat
          label="Sin gestionar"
          value={totals?.noGestionados}
          icon={Clock}
          tone="warning"
          loading={isLoading}
          to={tableLink('noGestionados')}
        />
        <Stat
          label="Vencidos"
          value={totals?.vencidos}
          icon={AlertTriangle}
          tone="danger"
          loading={isLoading}
          to={tableLink('vencidos')}
        />
        <Stat
          label="Al día"
          value={totals?.alDia}
          icon={CheckCircle2}
          tone="success"
          loading={isLoading}
          to={tableLink('alDia')}
        />
        <Stat
          label="Cerrados"
          value={totals?.inactivos}
          icon={Archive}
          tone="muted"
          loading={isLoading}
          to={tableLink('inactivos')}
          hint="Cerrados o desistidos: no entran en «Leads activos»"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {isLoading &&
          [0, 1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        {projects.map((p) => (
          <Card key={p.id} data-testid="project-card">
            <CardHeader>
              <CardTitle>{p.name}</CardTitle>
              <CardDescription>
                {p.companyName}
                {p.district ? ` · ${p.district}` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-5 gap-2 text-center">
                <Metric
                  label="Activos"
                  value={p.activos}
                  to={tableLink('activos', { companyId: p.companyId, projectId: p.id })}
                />
                <Metric
                  label="Sin gestionar"
                  value={p.noGestionados}
                  tone="warning"
                  to={tableLink('noGestionados', { companyId: p.companyId, projectId: p.id })}
                />
                <Metric
                  label="Vencidos"
                  value={p.vencidos}
                  tone="danger"
                  to={tableLink('vencidos', { companyId: p.companyId, projectId: p.id })}
                />
                <Metric
                  label="Al día"
                  value={p.alDia}
                  tone="success"
                  to={tableLink('alDia', { companyId: p.companyId, projectId: p.id })}
                />
                <Metric
                  label="Cerrados"
                  value={p.inactivos}
                  tone="muted"
                  to={tableLink('inactivos', { companyId: p.companyId, projectId: p.id })}
                />
              </dl>
            </CardContent>
          </Card>
        ))}
        {data && projects.length === 0 && (
          <Card className="md:col-span-2">
            <CardContent className="text-muted-foreground p-8 text-center text-sm">
              {data.projects.length === 0
                ? 'Aún no tienes proyectos asignados. Pide al administrador que te agregue a una empresa o proyecto.'
                : 'La empresa activa no tiene proyectos visibles. Cambia de empresa en el selector de arriba.'}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

const tones = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  muted: 'text-muted-foreground',
} as const;

const interactive =
  'hover:bg-accent/50 focus-within:ring-ring transition-colors focus-within:ring-2';

function Stat({
  label,
  value,
  icon: Icon,
  tone = 'default',
  loading,
  to,
  hint,
}: {
  label: string;
  value?: number;
  icon: typeof Inbox;
  tone?: keyof typeof tones;
  loading: boolean;
  to: string;
  hint?: string;
}) {
  return (
    <Card className={interactive}>
      <Link to={to} className="block" title={hint ?? `${label}: ver en la tabla`}>
        <CardContent className="flex items-center justify-between p-5">
          <div>
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {label}
            </p>
            {loading ? (
              <Skeleton className="mt-1 h-7 w-16" />
            ) : (
              <p className={`text-2xl font-semibold ${tones[tone]}`}>
                {value === undefined ? '—' : numberFormat.format(value)}
              </p>
            )}
          </div>
          <Icon className={`h-5 w-5 ${tones[tone]}`} />
        </CardContent>
      </Link>
    </Card>
  );
}

function Metric({
  label,
  value,
  tone = 'default',
  to,
}: {
  label: string;
  value: number;
  tone?: keyof typeof tones;
  to: string;
}) {
  return (
    <div>
      <dt className="text-muted-foreground text-[11px]">{label}</dt>
      <dd className={`text-lg font-semibold ${tones[tone]}`}>
        <Link to={to} className="hover:underline" title={`${label}: ver en la tabla`}>
          {numberFormat.format(value)}
        </Link>
      </dd>
    </div>
  );
}
